export type ReactionType = "like" | "love" | "haha" | "wow" | "fire" | "celebrate" | "listen" | "thanks" | "hundred" | "rock" | "watching";

/**
 * Display order for the picker, and precedence for getMyReaction().
 *
 * The original six come first and keep their order — they are what existing members
 * already reach for. The five after them were added for the Tone Master Pro Discord
 * corpus, where they were among the most-used reactions and had no honest home in
 * the original set.
 */
export const REACTION_TYPES: ReactionType[] = ["like", "love", "haha", "wow", "fire", "celebrate", "listen", "thanks", "hundred", "rock", "watching"];

export const REACTION_EMOJI: Record<ReactionType, string> = {
  like: "👍",
  love: "❤️",
  haha: "😂",
  wow: "😮",
  fire: "🔥",
  celebrate: "🎉",
  listen: "👂",
  thanks: "🙏",
  hundred: "💯",
  rock: "🤘",
  watching: "👀",
};

export const REACTION_LABELS: Record<ReactionType, string> = {
  like: "Like",
  love: "Love",
  haha: "Haha",
  wow: "Wow",
  fire: "Fire",
  celebrate: "Celebrate",
  listen: "Listen",
  thanks: "Thanks",
  hundred: "100",
  rock: "Rock on",
  watching: "Watching",
};

const REACTION_TYPE_SET = new Set<string>(REACTION_TYPES);

/** userId → distinct reaction types for that user */
export type ReactionMap = Record<string, ReactionType[]>;

export type ReactionCountItem = {
  type: ReactionType;
  emoji: string;
  count: number;
};

export type ReactionDoc = {
  reactions?: Record<string, string | string[]>;
  likes?: string[];
};

function isReactionType(value: string): value is ReactionType {
  return REACTION_TYPE_SET.has(value);
}

function normalizeUserReactions(value: string | string[] | null | undefined): ReactionType[] {
  if (value == null) return [];
  const raw = Array.isArray(value) ? value : [value];
  const seen = new Set<ReactionType>();
  for (const item of raw) {
    if (typeof item === "string" && isReactionType(item)) {
      seen.add(item);
    }
  }
  return REACTION_TYPES.filter((type) => seen.has(type));
}

export function normalizeReactions(doc: ReactionDoc | null | undefined): ReactionMap {
  if (!doc) return {};

  const normalized: ReactionMap = {};

  if (Array.isArray(doc.likes)) {
    for (const userId of doc.likes) {
      if (userId) normalized[userId] = ["like"];
    }
  }

  if (doc.reactions && typeof doc.reactions === "object") {
    for (const [userId, value] of Object.entries(doc.reactions)) {
      if (!userId) continue;
      const types = normalizeUserReactions(value);
      if (types.length) {
        normalized[userId] = types;
      } else {
        delete normalized[userId];
      }
    }
  }

  return normalized;
}

export function getReactionCounts(reactions: ReactionMap): ReactionCountItem[] {
  const counts: Partial<Record<ReactionType, number>> = {};

  for (const types of Object.values(reactions)) {
    for (const type of types) {
      counts[type] = (counts[type] || 0) + 1;
    }
  }

  return REACTION_TYPES.filter((type) => (counts[type] || 0) > 0).map((type) => ({
    type,
    emoji: REACTION_EMOJI[type],
    count: counts[type] as number,
  }));
}

/**
 * The reactions a post's summary draws as bubbles: the `max` most used, ties in
 * REACTION_TYPES order — except that one of YOURS always makes the cut. Otherwise a reaction
 * you just left on a busy post shows nowhere on the row, and only the total moving says it
 * landed. When none of yours is in the top `max`, your most used one takes the last place.
 *
 * Shared so the web control and the app's strip pick the same bubbles.
 */
export function summaryReactions<T extends { type: ReactionType; count: number }>(counts: T[], mine: Iterable<ReactionType>, max = 3): T[] {
  const mineSet = new Set<ReactionType>(mine || []);
  const ranked = [...(counts || [])].sort((a, b) => b.count - a.count || REACTION_TYPES.indexOf(a.type) - REACTION_TYPES.indexOf(b.type));
  const top = ranked.slice(0, max);
  if (!mineSet.size || top.some((c) => mineSet.has(c.type))) return top;
  const yours = ranked.find((c) => mineSet.has(c.type));
  if (!yours) return top;
  if (top.length < max) return [...top, yours];
  return [...top.slice(0, max - 1), yours];
}

/**
 * A `{ type: false }` map covering every reaction type.
 *
 * Derived rather than written out, so adding a type to REACTION_TYPES cannot leave
 * a stale literal behind — which is exactly what adding `listen` did.
 */
export function emptyReactionSet(): Record<ReactionType, boolean> {
  return REACTION_TYPES.reduce(
    (set, type) => {
      set[type] = false;
      return set;
    },
    {} as Record<ReactionType, boolean>
  );
}

export function getMyReactions(reactions: ReactionMap, userId: string | null | undefined): ReactionType[] {
  if (!userId || !reactions[userId]) return [];
  const set = new Set(reactions[userId]);
  return REACTION_TYPES.filter((type) => set.has(type));
}

/** Primary reaction for display (first in REACTION_TYPES order), or null. */
export function getMyReaction(reactions: ReactionMap, userId: string | null | undefined): ReactionType | null {
  const mine = getMyReactions(reactions, userId);
  return mine[0] || null;
}

export function hasReaction(reactions: ReactionMap, userId: string | null | undefined, type: ReactionType): boolean {
  if (!userId) return false;
  return (reactions[userId] || []).includes(type);
}

export function getUserIdsForReaction(reactions: ReactionMap, type: ReactionType): string[] {
  return Object.entries(reactions)
    .filter(([, types]) => types.includes(type))
    .map(([userId]) => userId);
}

/** Toggle `selected` in the user's set: add if missing, remove if present. */
export function toggleReaction(current: ReactionType[] | null | undefined, selected: ReactionType): ReactionType[] {
  const set = new Set(current || []);
  if (set.has(selected)) {
    set.delete(selected);
  } else {
    set.add(selected);
  }
  return REACTION_TYPES.filter((type) => set.has(type));
}

/**
 * Toggle `selectedType` for `userId`. Removes the user key when their set becomes empty.
 */
export function applyReactionLocally(reactions: ReactionMap, userId: string, selectedType: ReactionType): ReactionMap {
  if (!userId) return { ...reactions };
  const next = { ...reactions };
  const updated = toggleReaction(next[userId], selectedType);
  if (updated.length) {
    next[userId] = updated;
  } else {
    delete next[userId];
  }
  return next;
}

/**
 * Apply one user's final reaction set to a post document IN PLACE — the optimistic,
 * local half of `setReaction`, which writes the same two fields to Firestore.
 *
 * Both fields, always. Every reaction is dual-written into the legacy `likes` array (the
 * Firestore rules require it), and `normalizeReactions` reads anyone in `likes` with no
 * `reactions` entry as a 👍 — that's how pre-reaction likes still show. So clearing only
 * `reactions` when someone removed their last reaction left them in the local `likes`, and
 * they instantly read back as a 👍 they couldn't take off.
 */
export function setUserReactionsOnDoc(doc: ReactionDoc & { likeCount?: number }, userId: string, types: ReactionType[]): void {
  if (!doc || !userId) return;
  const reactions = { ...(doc.reactions || {}) };
  const likes = (Array.isArray(doc.likes) ? doc.likes : []).filter((id) => id !== userId);
  if (types && types.length) {
    reactions[userId] = [...types];
    likes.push(userId);
  } else {
    delete reactions[userId];
  }
  doc.reactions = reactions;
  doc.likes = likes;
  doc.likeCount = Object.keys(normalizeReactions(doc)).length;
}

// Match emoji sequences including ZWJ, skin tones, variation selectors, and keycaps.
// Built lazily with `new RegExp` (NOT a regex literal) on purpose: V8 validates
// regex literals at script-compile time, and NativeScript's iOS runtime rejects
// Unicode property escapes (\p{...}) \u2014 a literal here fails the ENTIRE bundle at
// load. Deferring to runtime keeps the bundle loadable everywhere; on an engine
// without \p{...} support this resolves to null and callers degrade gracefully.
let _emojiOnlyPattern: RegExp | null | undefined;
function getEmojiOnlyPattern(): RegExp | null {
  if (_emojiOnlyPattern !== undefined) return _emojiOnlyPattern;
  try {
    _emojiOnlyPattern = new RegExp(
      "^(?:(?:\\p{Extended_Pictographic}|\\p{Emoji_Presentation}|\\d\\uFE0F?\\u20E3)(?:\\uFE0F|\\uFE0E)?(?:\\u200D(?:\\p{Extended_Pictographic}|\\p{Emoji_Presentation})(?:\\uFE0F|\\uFE0E)?)*|\\p{Emoji_Modifier})+$",
      "u"
    );
  } catch {
    _emojiOnlyPattern = null;
  }
  return _emojiOnlyPattern;
}

/**
 * True when the message content (after stripping simple HTML / whitespace) is only emoji.
 */
export function isEmojiOnlyMessage(message: string | null | undefined): boolean {
  if (!message) return false;

  const withoutTags = message.replace(/<[^>]*>/g, " ");
  const decoded = withoutTags
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#?\w+;/g, " ");

  const trimmed = decoded.replace(/\s+/g, "").trim();
  if (!trimmed) return false;

  const emojiOnlyPattern = getEmojiOnlyPattern();
  if (!emojiOnlyPattern) return false;

  return emojiOnlyPattern.test(trimmed);
}
